import type {DebouncedFunc} from 'lodash';
import {RefObject, useEffect} from 'react';
import {getIsTouchDevice} from '../../../../../../common/getIsTouchDevice';

export default function useScrollViewerWheelEvent({
  onPressHandler,
  onMoveHandler,
  onReleaseHandler,
  onWheelHandler,
  onKeyDownHandler,
  onScroll,
  isReached,
  isReachedRef,
}: {
  onPressHandler: (e: TouchEvent) => void;
  onMoveHandler?: (e: TouchEvent) => void;
  onReleaseHandler: (e: TouchEvent) => void;
  onScroll: DebouncedFunc<() => void>;
  onWheelHandler: DebouncedFunc<() => void>;
  onKeyDownHandler: (e: KeyboardEvent) => void;
  isReached: boolean;
  isReachedRef: RefObject<boolean>;
}) {
  const touchAvailable = getIsTouchDevice();

  useEffect(() => {
    let wheelTimer = 0;
    if (isReached) {
      if (touchAvailable) {
        window.addEventListener('touchstart', onPressHandler);
        onMoveHandler && window.addEventListener('touchmove', onMoveHandler);
        window.addEventListener('touchend', onReleaseHandler);
      }
      // 스크롤이 가장 아래로 내려가자 마자 wheel 이벤트 핸들러가 호출되는 경우를 방지하기 위해
      // 500ms 후에 핸들러를 연결합니다.
      wheelTimer = window.setTimeout(() => {
        if (isReachedRef.current) {
          window.addEventListener('wheel', onWheelHandler);
        }
      }, 500);
      window.addEventListener('keydown', onKeyDownHandler);
    } else {
      if (touchAvailable) {
        window.removeEventListener('touchstart', onPressHandler);
        onMoveHandler && window.removeEventListener('touchmove', onMoveHandler);
        window.removeEventListener('touchend', onReleaseHandler);
      }
      window.clearTimeout(wheelTimer);
      window.removeEventListener('wheel', onWheelHandler);
      window.removeEventListener('keydown', onKeyDownHandler);
      onWheelHandler.cancel();
    }

    return () => {
      if (touchAvailable) {
        window.removeEventListener('touchstart', onPressHandler);
        onMoveHandler && window.removeEventListener('touchmove', onMoveHandler);
        window.removeEventListener('touchend', onReleaseHandler);
      }
      window.clearTimeout(wheelTimer);
      window.removeEventListener('wheel', onWheelHandler);
      window.removeEventListener('keydown', onKeyDownHandler);
      onScroll.cancel();
      onWheelHandler.cancel();
    };
  }, [
    onScroll,
    onKeyDownHandler,
    onPressHandler,
    onReleaseHandler,
    onMoveHandler,
    onWheelHandler,
    isReached,
    isReachedRef,
    touchAvailable,
  ]);
}
